import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  useCreateBrandMutation,
  useUpdateBrandMutation,
  type BrandDetail,
  type BrandListItem,
} from '../brandsApi';
import { messageOf } from '../lib';

/**
 * Create or rename a brand — `brands.manage`. Deliberately the only mutating
 * control this dialog offers: deciding which branches belong to a brand is a
 * separate, tenant-admin-only act (`BranchesPanel`'s own note), and the slug
 * is immutable once created (`BrandsService.create`'s uniqueness check keys
 * on it), so editing mode drops that field rather than showing one that would
 * 409 on every save.
 */
export default function BrandFormDialog({
  brand,
  onClose,
  onCreated,
  onSaved,
}: {
  /** Omit to create; pass to rename/re-logo an existing brand. */
  brand?: BrandListItem | BrandDetail;
  onClose: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(brand?.name ?? '');
  const [slug, setSlug] = useState('');
  const [logoUrl, setLogoUrl] = useState(brand?.logoUrl ?? '');
  const [error, setError] = useState<string | null>(null);

  const [createBrand, { isLoading: creating }] = useCreateBrandMutation();
  const [updateBrand, { isLoading: updating }] = useUpdateBrandMutation();
  const saving = creating || updating;

  const submit = async () => {
    setError(null);
    if (!name.trim()) {
      setError(t('brands.form.nameRequired'));
      return;
    }
    try {
      if (brand) {
        await updateBrand({ brandId: brand.id, body: { name: name.trim(), logoUrl: logoUrl.trim() || undefined } }).unwrap();
        toast.success(t('brands.form.saved'));
        onSaved?.();
      } else {
        if (!slug.trim()) {
          setError(t('brands.form.slugRequired'));
          return;
        }
        const created = await createBrand({
          name: name.trim(),
          slug: slug.trim(),
          logoUrl: logoUrl.trim() || undefined,
        }).unwrap();
        toast.success(t('brands.form.created'));
        onCreated?.(created.id);
      }
      onClose();
    } catch (e) {
      const message = messageOf(e, t('brands.form.saveFailed'));
      setError(message);
      toast.error(message);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={brand ? t('brands.form.editTitle') : t('brands.form.createTitle')}
      className="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? t('common.loading') : brand ? t('common.save') : t('brands.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('brands.form.name')} required error={error && !name.trim() ? error : undefined}>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        {!brand && (
          <Field
            label={t('brands.form.slug')}
            required
            hint={t('brands.form.slugHint')}
            error={error && !slug.trim() ? error : undefined}
          >
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder={t('brands.form.slugPlaceholder')}
            />
          </Field>
        )}
        <Field label={t('brands.form.logoUrl')} hint={t('brands.form.logoUrlHint')}>
          <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} />
        </Field>
        {error && name.trim() && (brand || slug.trim()) && (
          <p className="text-sm text-[var(--destructive)]">{error}</p>
        )}
      </div>
    </Dialog>
  );
}
